import React, { useCallback } from 'react';
import { noop } from 'lodash';
import EditHomepageResource from 'routes/advanced-help-center/resources/edit-homepage';
import ViewHomepageResource from 'routes/advanced-help-center/resources/view-homepage';
import { editHomepageExperience } from 'view/advanced-help-center/edit-homepage/experiences';
import { viewHomepageExperience } from 'view/advanced-help-center/view-homepage/experiences';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { useRelayResource } from '@atlassian/help-center-common-util/resource-router';
import { getTraceIdFromRelayQueryReference } from '@atlassian/help-center-common-util/retrieve-relay-traceid';

interface LayoutNextErrorBoundaryProps {
    renderError?: (error: Error | FetchError) => React.ReactNode;
    id: string;
    screenName: ScreenName.VIEW_HOMEPAGE | ScreenName.EDIT_HOMEPAGE;
    children: React.ReactNode;
}

const LayoutNextErrorBoundary: React.FC<LayoutNextErrorBoundaryProps> = ({ screenName, id, renderError, children }) => {
    // TODO screenName is sometimes wrong for view homepage , this is because screenName is coming from withScreenName HOC, it can be fixed if we pass it from parent component, Ideally we expect this error boundary to have either view or edit homepage screenName which can be evaluated on the parent component
    const experience = screenName === ScreenName.EDIT_HOMEPAGE ? editHomepageExperience : viewHomepageExperience;
    const { queryReference } = useRelayResource(
        // @ts-ignore
        screenName === ScreenName.EDIT_HOMEPAGE ? EditHomepageResource : ViewHomepageResource
    );

    /**
     * If any error happens inside layout viewer/builder
     * 1. It should fail the entire experience - UFO
     * 2. Return null by default
     * 3. Send error payload to sentry and analytics - through ErrorBoundary
     * 4. Pass reason for the failure -UFO
     */
    const markExperienceFailed = useCallback(
        (error: Error | FetchError) => {
            if (!isFetchNetworkOrClientError(error as FetchError)) {
                const traceId = getTraceIdFromRelayQueryReference(queryReference);
                experience
                    .failure({
                        metadata: {
                            id,
                            traceId,
                            error: error?.message,
                            statusCode: (error as FetchError)?.status,
                            failureEvent: screenName,
                        },
                    })
                    .then(noop)
                    .catch(noop);
            }
        },
        [experience, id, screenName, queryReference]
    );

    return (
        <ErrorBoundary packageName={screenName} id={id} renderError={renderError} onError={markExperienceFailed}>
            {React.Children.only(children)}
        </ErrorBoundary>
    );
};

export default LayoutNextErrorBoundary;
